import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PersonAdd, PersonRemoveOutlined } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/EnterpriseAuthContext';

const ShareProjectDialog = ({ open, onClose, project }) => {
  const { state: authState } = useAuth();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // States for the user search input
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!project?.id) return;
      const { data, error } = await supabase
        .from('project_members')
        .select(`
          id,
          role,
          profiles(id, username, email)
        `)
        .eq('project_id', project.id);

      if (error) throw error;
      setMembers(
        (data || [])
          .filter((m) => m?.profiles)
          .map((m) => ({
            projectMemberId: m.id,
            role: m.role,
            id: m.profiles.id,
            username: m.profiles.username ?? '',
            email: m.profiles.email ?? ''
          }))
      );
    } catch (err) {
      setError('Failed to load project members.');
    } finally {
      setLoading(false);
    }
  }, [project?.id]); // Add project.id as a dependency

  useEffect(() => {
    if (open) {
      loadMembers();
    }
  }, [project, open, loadMembers]); // Add loadMembers to the dependency array

  const fetchUsersForSharing = useCallback(async (searchTerm) => {
    if (!authState.activeAccount?.id || !authState.user?.id) {
      setOptions([]);
      return;
    }
    const existingMemberIds = members.map((m) => m.id);
    const { data, error } = await supabase.rpc('get_users_for_sharing', {
      p_account_id: authState.activeAccount.id,
      p_search_term: searchTerm ?? '',
      p_exclude_ids: [authState.user.id, ...existingMemberIds]
    });

    if (error) {
      console.error('Error fetching users:', error);
      setOptions([]);
      return;
    }
    setOptions(data ?? []);
  }, [members, authState.activeAccount?.id, authState.user?.id]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchUsersForSharing(inputValue);
    }, inputValue === '' ? 0 : 500);
    return () => clearTimeout(debounce);
  }, [inputValue, fetchUsersForSharing]);


  const handleAddMember = async () => {
    if (!selectedUser) {
      setError('Please select a user to add.');
      return;
    }
    setError('');
    setSuccess('');

    try {
      const { error } = await supabase
        .from('project_members')
        .insert({
          project_id: project.id,
          user_id: selectedUser.id,
          role: 'editor', // Default role
        });
      
      if (error) throw error;
      
      setSuccess(`${selectedUser.username} has been added to the project.`);
      setSelectedUser(null);
      setInputValue('');
      loadMembers(); // Refresh the list
    } catch (err) {
      setError('Failed to add user to the project.');
      console.error(err);
    }
  };

  const handleRemoveMember = async (member) => {
    setError('');
    setSuccess('');

    try {
       const { error } = await supabase
        .from('project_members')
        .delete()
        .eq('id', member.projectMemberId);

      if (error) throw error;
      
      setSuccess(`${member.username} has been removed from the project.`);
      loadMembers(); // Refresh the list
    } catch (err) {
      setError('Failed to remove user.');
      console.error(err);
    }
  };

  const initials = (name) => (String(name || '?').trim().charAt(0) || '?').toUpperCase();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isPhone} disableRestoreFocus>
      <DialogTitle>Share "{project?.name}"</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Typography variant="subtitle2" component="h3" sx={{ mt: 1 }}>Add people</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Teammates in your workspace can be added as editors of this project.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          <Autocomplete
            fullWidth
            getOptionLabel={(option) => option?.username ?? option?.email ?? ''}
            filterOptions={(x) => x}
            options={options}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={selectedUser}
            onChange={(event, newValue) => {
              setSelectedUser(newValue);
            }}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Name or email" fullWidth />
            )}
            renderOption={(props, option) => {
              const { key: _key, ...otherProps } = props;
              return (
                <li key={option?.id ?? _key} {...otherProps}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>{initials(option.username || option.email)}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{option.username}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">{option.email}</Typography>
                    </Box>
                  </Box>
                </li>
              );
            }}
            onOpen={() => {
              if (options.length === 0 && authState.activeAccount?.id) {
                fetchUsersForSharing(inputValue || '');
              }
            }}
            noOptionsText="No other users in your workspace yet. Invite teammates from the Admin page first."
          />
          <Button
            onClick={handleAddMember}
            variant="contained"
            startIcon={<PersonAdd />}
            disabled={!selectedUser}
            sx={{ minHeight: 40, flex: 'none', width: { xs: '100%', sm: 'auto' } }}
          >
            Add
          </Button>
        </Box>

        <Typography variant="subtitle2" component="h3" sx={{ mt: 3, mb: 1 }}>
          People with access
        </Typography>
        {loading ? (
          <Box aria-busy="true" aria-label="Loading members">
            {[0, 1].map((i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="60%" height={14} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <List disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 'var(--q-radius-xl)' }}>
            <ListItem divider={members.length > 0}>
              <ListItemAvatar sx={{ minWidth: 44 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'primary.main' }}>
                  {initials(authState.user?.username)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={authState.user?.username ?? ''}
                secondary={authState.user?.email ?? ''}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
              <Chip size="small" label="Owner" color="primary" />
            </ListItem>
            {members.map((member, index) => (
              <ListItem
                key={member.id}
                divider={index < members.length - 1}
                secondaryAction={
                  <Tooltip title="Remove access">
                    <IconButton edge="end" aria-label={`Remove ${member.username || member.email}`} onClick={() => handleRemoveMember(member)}>
                      <PersonRemoveOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{initials(member.username || member.email)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={member.username || member.email}
                  secondary={[member.email, member.role].filter(Boolean).join(' · ')}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                  sx={{ pr: 4 }}
                />
              </ListItem>
            ))}
          </List>
        )}
        {!loading && members.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Only you have access to this project.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareProjectDialog;
